import { IMenuStyleData } from "@pageBuilder/typings";
import { CssProperties } from "../../../../models/css-property";
import { ElementStyleService } from "../../../../services/element-style.service";

export abstract class BaseItemsStylesRenderer {

    constructor(protected readonly styleService: ElementStyleService) {
    }

    public render(element: HTMLElement, style: IMenuStyleData): void {
        this.renderFontStyle(element, style);
        this.renderFontColorStyle(element, style);
        this.renderFontActiveColorStyle(element, style);
        this.renderFontHoverColorStyle(element, style);
    }

    private getNavItemSelector(id: string): string {
        return `#${id} li`;
    }

    private getNavItemNameSelector(id: string): string {
        return `#${id} .nav-item-name`;
    }

    protected getNavItemNameHoverSelector(id: string): string {
        return `#${id} ul.root > li:hover > .nav-item-name`;
    }

    protected getNavItemNameNotHoverSelector(id: string): string {
        return `#${id} ul.root > li:not(:hover) > .nav-item-name`;
    }

    protected getActiveNavItemNameSelector(id: string): string {
        return `#${id} ul.root > li.active:not(:hover) > .nav-item-name`;
    }

    protected renderFontStyle(element: HTMLElement, style: IMenuStyleData): void {
        const fontStyleProperties = this.getNavSpanProperties(style);
        if (fontStyleProperties.data.length > 0) {
            this.styleService.setStyle(element, this.getNavItemNameSelector(element.id), fontStyleProperties.data);
        }

        // Need to set font-size two times due to letter-spacing setting in site-design
        const fontSizeProperties = this.getFontSizeProperties(style);
        if (fontSizeProperties.data.length > 0) {
            this.styleService.setStyle(element, this.getNavItemSelector(element.id), fontSizeProperties.data);
        }
    }

    protected renderFontColorStyle(element: HTMLElement, style: IMenuStyleData): void {
        const fontColorProperties = this.getNavSpanNonHoveredProperties(style);
        if (fontColorProperties.data.length > 0) {
            this.styleService.setStyle(element, this.getNavItemNameNotHoverSelector(element.id), fontColorProperties.data);
        }
    }

    protected renderFontActiveColorStyle(element: HTMLElement, style: IMenuStyleData): void {
        const fontHoverColorProperties = this.getNavSpanHoveredProperties(style);
        if (fontHoverColorProperties.data.length > 0) {
            this.styleService.setStyle(element, this.getNavItemNameHoverSelector(element.id), fontHoverColorProperties.data);
        }
    }

    protected renderFontHoverColorStyle(element: HTMLElement, style: IMenuStyleData): void {
        const fontActiveColorProperties = this.getActiveNavSpanProperties(style);
        if (fontActiveColorProperties.data.length > 0) {
            this.styleService.setStyle(element, this.getActiveNavItemNameSelector(element.id), fontActiveColorProperties.data);
        }
    }

    protected getNavSpanProperties(style: IMenuStyleData): CssProperties {
        const cssProperties = new CssProperties();
        if (style?.font) {
            cssProperties.add("font-family", style?.font?.family ?? "");
            cssProperties.add("font-weight", style?.font?.weight ?? "");
        }

        const fontSizeProperties = this.getFontSizeProperties(style);
        fontSizeProperties.data.forEach(cssProperty => {
            if (cssProperty.value) {
                cssProperties.add(cssProperty.property, cssProperty.value);
            }
        });

        return cssProperties;
    }

    protected getFontSizeProperties(style: IMenuStyleData): CssProperties {
        const cssProperties = new CssProperties();
        if (style?.font) {
            cssProperties.add("font-size", style?.font?.size ?? "");
            if (style?.font?.lineHeight) {
                cssProperties.add("line-height", style?.font?.lineHeight ?? "");
            }
        }

        return cssProperties;
    }

    protected getNavSpanNonHoveredProperties(style: IMenuStyleData): CssProperties {
        const cssProperties = new CssProperties();
        if (style?.linkColorStyleData?.colorDefault) {
            cssProperties.add("color", style?.linkColorStyleData?.colorDefault);
        }

        return cssProperties;
    }

    protected getActiveNavSpanProperties(style: IMenuStyleData): CssProperties {
        const cssProperties = new CssProperties();
        if (style?.linkColorStyleData?.colorClicked) {
            cssProperties.add("color", style?.linkColorStyleData?.colorClicked);
        }

        return cssProperties;
    }

    protected getNavSpanHoveredProperties(style: IMenuStyleData): CssProperties {
        const cssProperties = new CssProperties();
        if (style?.linkColorStyleData?.colorHovered) {
            cssProperties.add("color", style?.linkColorStyleData?.colorHovered);
        }

        return cssProperties;
    }

    public getStylesSelectors(id: string): string[] {
        return [
            ...this.getCommonSelectors(id),
            ...this.getStyleSpecificSelectors(id)
        ];
    }

    private getCommonSelectors(id: string): string[] {
        return [
            this.getNavItemNameSelector(id),
            this.getNavItemNameHoverSelector(id),
            this.getNavItemNameNotHoverSelector(id),
            this.getActiveNavItemNameSelector(id),
            this.getNavItemSelector(id)
        ];
    }

    public abstract getStyleSpecificSelectors(id: string): Array<string>;

}
